import { Select, Sizes } from '@sede-enterprise/shell-ds-react-framework';
import { useEffect, useState } from 'react';
import { sendNotification } from 'src/UI/Notification';
import { COMMON_MESSAGES } from 'src/constants/Common';
import { GET_CONTRACT_TYPES, GET_ORGANIZATIONS } from 'src/constants/EUMarkets';
import { get } from 'src/services/GenesisApi';
import type { IOrganization } from 'src/types/EUMarkets.type';

type Props = {
  organizationIds: string[];
  onOrganizationIdsChange: (ids: string[]) => void;
  contractTypes?: string[];
  onContractTypesChange?: (names: string[]) => void;
};

type FilterOption = { value: string; label: string };
type ContractType = { contractTypeName: string };

const PositionSearchFilters = ({
  organizationIds,
  onOrganizationIdsChange,
  contractTypes,
  onContractTypesChange,
}: Props) => {
  const [organizationOptions, setOrganizationOptions] = useState<FilterOption[]>([]);
  const [contractTypeOptions, setContractTypeOptions] = useState<FilterOption[]>([]);
  const [loading, setLoading] = useState(true);
  const showContractTypes = contractTypes !== undefined;

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      get(GET_ORGANIZATIONS),
      showContractTypes ? get(GET_CONTRACT_TYPES) : Promise.resolve(null),
    ])
      .then(([organizations, types]) => {
        if (!active) {
          return;
        }
        setOrganizationOptions(
          (organizations.data as IOrganization[])
            .filter((organization) =>
              organization.roles?.some((role) => role.roleName === 'Internal'),
            )
            .map((organization) => ({
              value: String(organization.id),
              label: organization.organizationName,
            })),
        );
        if (types) {
          setContractTypeOptions(
            (types.data as ContractType[]).map((type) => ({
              value: type.contractTypeName,
              label: type.contractTypeName,
            })),
          );
        }
      })
      .catch(() => {
        if (active) {
          sendNotification({ type: 'error', message: COMMON_MESSAGES.ERROR });
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [showContractTypes]);

  return (
    <>
      <div className='filter-field search-filter-field legal-entity-filter-field'>
        <span className='filter-label'>Legal Entity</span>
        <Select
          aria-label='Legal Entity'
          data-testid='position-legal-entity-select'
          size={Sizes.Small}
          mode='multiple'
          placeholder='All Legal Entities'
          options={organizationOptions}
          optionLabelProp='label'
          value={organizationIds}
          onChange={(value) => onOrganizationIdsChange((value as string[]) ?? [])}
          loading={loading}
          disabled={loading}
          allowClear
          showSearch
          optionFilterProp='label'
        />
      </div>
      {showContractTypes ? (
        <div className='filter-field search-filter-field'>
          <span className='filter-label'>Contract Type</span>
          <Select
            aria-label='Contract Type'
            data-testid='position-contract-type-select'
            size={Sizes.Small}
            mode='multiple'
            placeholder='All Contract Types'
            options={contractTypeOptions}
            optionLabelProp='label'
            value={contractTypes}
            onChange={(value) => onContractTypesChange?.((value as string[]) ?? [])}
            loading={loading}
            disabled={loading}
            allowClear
            showSearch
            optionFilterProp='label'
          />
        </div>
      ) : null}
    </>
  );
};

export default PositionSearchFilters;
